import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { ChevronDown, ChevronUp, Calendar, X } from 'lucide-react'
import Card from '../../../components/Card'

interface MonthRange {
  startMonth: number
  endMonth: number
}

interface PeriodSelectorProps {
  onPeriodChange: (year: number, monthRanges: MonthRange[]) => void
  className?: string
  initialYear?: number
  initialMonthRanges?: MonthRange[]
}

const PeriodSelector: React.FC<PeriodSelectorProps> = ({
  onPeriodChange,
  className = '',
  initialYear,
  initialMonthRanges
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [selectedYear, setSelectedYear] = useState<number>(initialYear || new Date().getFullYear())
  const [selectedMonths, setSelectedMonths] = useState<number[]>(() => {
    if (initialMonthRanges && initialMonthRanges.length > 0) {
      // Converter monthRanges para array de meses
      const months: number[] = []
      initialMonthRanges.forEach(range => {
        for (let month = range.startMonth; month <= range.endMonth; month++) {
          months.push(month)
        }
      })
      return months
    }
    return []
  })

  const months = [
    { number: 1, name: 'Jan', short: 'Jan' },
    { number: 2, name: 'Fev', short: 'Fev' },
    { number: 3, name: 'Mar', short: 'Mar' },
    { number: 4, name: 'Abr', short: 'Abr' },
    { number: 5, name: 'Mai', short: 'Mai' },
    { number: 6, name: 'Jun', short: 'Jun' },
    { number: 7, name: 'Jul', short: 'Jul' },
    { number: 8, name: 'Ago', short: 'Ago' },
    { number: 9, name: 'Set', short: 'Set' },
    { number: 10, name: 'Out', short: 'Out' },
    { number: 11, name: 'Nov', short: 'Nov' },
    { number: 12, name: 'Dez', short: 'Dez' }
  ]

  const years = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - 5 + i)

  const handleMonthClick = (monthNumber: number) => {
    setSelectedMonths(prev => {
      if (prev.includes(monthNumber)) {
        return prev.filter(m => m !== monthNumber)
      } else {
        return [...prev, monthNumber].sort()
      }
    })
  }

  const createMonthRanges = (months: number[]): MonthRange[] => {
    if (months.length === 0) return []
    
    const sortedMonths = [...months].sort()
    const ranges: MonthRange[] = []
    let start = sortedMonths[0]
    let end = sortedMonths[0]

    for (let i = 1; i < sortedMonths.length; i++) {
      if (sortedMonths[i] === end + 1) {
        end = sortedMonths[i]
      } else {
        ranges.push({ startMonth: start, endMonth: end })
        start = sortedMonths[i]
        end = sortedMonths[i]
      }
    }
    ranges.push({ startMonth: start, endMonth: end })
    return ranges
  }

  const clearSelection = () => {
    setSelectedMonths([])
  }

  const selectAllMonths = () => {
    const allMonths = months.map(month => month.number)
    setSelectedMonths(allMonths)
  }

  // Notificar mudanças automaticamente quando ano ou meses mudarem
  useEffect(() => {
    if (selectedYear > 0 && selectedMonths.length > 0) {
      const ranges = createMonthRanges(selectedMonths)
      onPeriodChange(selectedYear, ranges)
    } else {
      // Se não houver seleção, notificar com arrays vazios
      onPeriodChange(selectedYear, [])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, selectedMonths])

  return (
    <div className={`w-full ${className}`}>
      <Card className="p-4" hover={false}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-1 rounded-lg transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
              style={{ color: 'var(--text-secondary)' }}
            >
              {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
            </button>
            <Calendar className="h-5 w-5" style={{ color: 'var(--primary)' }} />
            <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
              Período de Análise
            </h3>
            {selectedMonths.length > 0 && (
              <span className="text-sm px-2 py-1 rounded-full" style={{ 
                backgroundColor: 'rgba(59, 130, 246, 0.1)', 
                color: '#3b82f6' 
              }}>
                {selectedMonths.length} {selectedMonths.length === 1 ? 'mês' : 'meses'} selecionado{selectedMonths.length > 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>

        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="space-y-6">
              {/* Ano e Meses Selection - Mesma linha */}
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                {/* Ano Selection */}
                <div className="lg:col-span-1">
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                    Selecionar Ano
                  </label>
                  <div className="relative">
                    <select
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(Number(e.target.value))}
                      className="w-full p-3 rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                      style={{
                        backgroundColor: 'var(--bg-card)',
                        color: 'var(--text-primary)',
                        border: '1px solid var(--border-primary)',
                        minWidth: '120px'
                      }}
                    >
                      {years.map(year => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                    <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 pointer-events-none" style={{ color: 'var(--text-secondary)' }} />
                  </div>
                </div>

                {/* Meses Selection */}
                {selectedYear > 0 && (
                  <div className="lg:col-span-3">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <label className="block text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                          Selecionar Meses
                        </label>
                        <button
                          onClick={selectAllMonths}
                          className="flex items-center space-x-1 text-sm text-blue-500 hover:text-blue-600 transition-colors"
                        >
                          <span>Selecionar Todos</span>
                        </button>
                      </div>
                      {selectedMonths.length > 0 && (
                        <button
                          onClick={clearSelection}
                          className="flex items-center space-x-1 text-sm text-red-500 hover:text-red-600 transition-colors"
                        >
                          <X className="h-4 w-4" />
                          <span>Limpar</span>
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                      {months.map(month => {
                        const isSelected = selectedMonths.includes(month.number)
                        return (
                          <motion.button
                            key={month.number}
                            onClick={() => handleMonthClick(month.number)}
                            className={`p-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                              isSelected
                                ? 'shadow-lg transform scale-105'
                                : 'hover:scale-105 hover:shadow-md'
                            }`}
                            style={{
                              backgroundColor: isSelected ? '#163965' : 'var(--bg-card)',
                              color: isSelected ? '#ffffff' : 'var(--text-primary)',
                              border: isSelected ? '1px solid #163965' : '1px solid var(--border-primary)'
                            }}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            {month.short}
                          </motion.button>
                        )
                      })}
                    </div>
                    {selectedMonths.length > 0 && (
                      <div className="mt-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
                        Meses selecionados: {selectedMonths.map(m => months[m-1].short).join(', ')}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </Card>
    </div>
  )
}

export default PeriodSelector

